"use client";

/**
 * Big gradient promo card pitching PWA install to mobile visitors.
 * Mirrors the visual language of the old desktop extension promo - this
 * one is its mobile twin: only appears on phones, hides when the app
 * is already running as a PWA.
 *
 * Three render states:
 *   - installed           → "Already installed" badge
 *   - canInstall          → "Install" button triggers the native prompt
 *   - not installable yet → "Show me how" link routing to /install
 *                           (also used while we're still waiting for
 *                           Chrome's beforeinstallprompt to decide)
 */
import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "@/lib/localized-link";
import { Smartphone, Share as ShareIcon, Download } from "lucide-react";
import { useT } from "@/lib/locale-context";
import { useInstallPrompt } from "@/lib/use-install-prompt";

export default function PWAInstallPromo() {
  const t = useT();
  const { canInstall, installed, resolving, promptInstall } = useInstallPrompt();
  // Gate the UA-dependent render behind a mount flag so the SSR
  // output matches the first client paint. Previously the server
  // always rendered the <section> (no navigator), and the client
  // stripped it on desktop via `typeof window !== undefined` -
  // classic hydration mismatch that regenerated the whole subtree
  // and spammed the console. Returning null on the server AND on
  // the pre-mount client keeps both trees identical through
  // hydration; the UA decision runs only post-mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  if (!mounted) return null;

  // Mobile-only - the desktop UA gate, inverted.
  // Desktop users get the extension; phone users get the PWA.
  const ua = navigator.userAgent;
  const isMobile = /Android|iPhone|iPad|iPod|Mobile|Mobi/i.test(ua);
  if (!isMobile) return null;

  // Already running as a PWA → nothing to pitch. Also avoids the
  // briefly-visible card on the very first PWA launch before we know.
  if (installed) return null;

  return (
    <section
      className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20"
      style={{ contentVisibility: "auto", containIntrinsicSize: "auto 280px" }}
    >
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 sm:p-10">
        {/* Decorative circles + backdrop-blur dropped - paint-time
            effects that hurt resize perf and read as templated. */}
        <div className="relative flex flex-col sm:flex-row items-center gap-8">
          {/* App icon inside a tinted tile - echoes the home-screen
              tile the user sees after install. next/image instead of
              <img> for the same resize-stability win as the hero. */}
          <div className="shrink-0 w-14 h-14 sm:w-20 sm:h-20 rounded-2xl bg-gray-100 flex items-center justify-center ring-1 ring-gray-200">
            <Image
              src="/logo.svg"
              alt=""
              aria-hidden
              width={56}
              height={56}
              className="rounded-xl"
            />
          </div>

          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
              {t("pwa.promo.title")}
            </h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed max-w-lg">
              {t("pwa.promo.desc")}
            </p>
          </div>

          <div className="shrink-0">
            {resolving ? (
              // Skeleton: identical shape to the resolved button so the
              // card doesn't re-flow when Chrome fires beforeinstall-
              // prompt a few hundred ms after mount.
              <div className="h-[52px] w-40 rounded-xl bg-gray-200 animate-pulse" />
            ) : canInstall ? (
              <button
                type="button"
                onClick={() => promptInstall()}
                className="flex items-center gap-2.5 px-6 py-3.5 bg-indigo-600 text-white rounded-xl font-semibold text-sm hover:bg-indigo-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                {t("pwa.promo.cta.install")}
              </button>
            ) : (
              <Link
                href="/install"
                className="flex items-center gap-2.5 px-6 py-3.5 bg-indigo-600 text-white rounded-xl font-semibold text-sm hover:bg-indigo-700 transition-colors"
              >
                <Smartphone className="w-5 h-5" />
                {t("pwa.promo.cta.howto")}
              </Link>
            )}
          </div>
        </div>

        {/* Feature pills - three quick benefits that make the value
            concrete. */}
        <div className="relative flex flex-wrap justify-center sm:justify-start gap-3 mt-6 sm:mt-8 sm:ml-28">
          <Pill icon={<ShareIcon className="w-3 h-3" />}>{t("pwa.promo.f1")}</Pill>
          <Pill>{t("pwa.promo.f2")}</Pill>
          <Pill>{t("pwa.promo.f3")}</Pill>
        </div>
      </div>
    </section>
  );
}

function Pill({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-gray-700 font-medium ring-1 ring-gray-200">
      {icon}
      {children}
    </span>
  );
}
